import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiX, FiDollarSign, FiCalendar, FiUser, FiClock, FiUsers, FiTrendingUp, FiSearch } from 'react-icons/fi';
import axiosConfig from '../../../api/axiosConfig';

const MONTH_OPTIONS = [
  { value: 1, label: 'Ocak' },
  { value: 2, label: 'Subat' },
  { value: 3, label: 'Mart' },
  { value: 4, label: 'Nisan' },
  { value: 5, label: 'Mayis' },
  { value: 6, label: 'Haziran' },
  { value: 7, label: 'Temmuz' },
  { value: 8, label: 'Agustos' },
  { value: 9, label: 'Eylul' },
  { value: 10, label: 'Ekim' },
  { value: 11, label: 'Kasim' },
  { value: 12, label: 'Aralik' }
];

const TeacherPaymentDetailsModal = ({ isOpen, onClose }) => {
  const today = useMemo(() => new Date(), []);
  const currentMonth = today.getMonth() + 1;
  const currentYear = today.getFullYear();

  const [loading, setLoading] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState([]);
  const [allPaymentDetails, setAllPaymentDetails] = useState([]); 
  const [totalStats, setTotalStats] = useState({
    totalLessonRevenue: 0,
    totalCompanyRevenue: 0,
    totalConsultantPayments: 0,
    lessonCount: 0
  });
  const [filterType, setFilterType] = useState('daily');
  const [teachers, setTeachers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Queue sistemi için yeni state'ler
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [totalToProcess, setTotalToProcess] = useState(0);
  const processingQueueRef = useRef(null);
  const abortControllerRef = useRef(null);

  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const yearOptions = useMemo(() => {
    return Array.from({ length: 8 }, (_, index) => currentYear - 3 + index);
  }, [currentYear]);

  const filteredPaymentDetails = useMemo(() => {
    const trimmedSearch = searchTerm.trim();
    if (!trimmedSearch) return allPaymentDetails;

    const searchLower = trimmedSearch.toLowerCase();
    return allPaymentDetails.filter((teacher) => {
      const teacherName = teacher.name?.toLowerCase() || '';
      const teacherPhone = String(teacher.phone || teacher.telefon || '');
      const teacherEmail = teacher.email?.toLowerCase() || teacher.e_posta?.toLowerCase() || '';
      return (
        teacherName.includes(searchLower) ||
        teacherPhone.includes(trimmedSearch) ||
        teacherEmail.includes(searchLower)
      );
    });
  }, [allPaymentDetails, searchTerm]);

  const isTeacherActive = (teacher) => {
    const status = String(teacher?.status ?? '').toLowerCase();
    return status === '1' || status === 'aktif' || status === 'active';
  };

  const mergeWithAllActiveTeachers = (teacherDetails, teacherList = []) => {
    const existingIds = new Set(teacherDetails.map((teacher) => String(teacher.id)));
    const missingTeachers = teacherList
      .filter((teacher) => !existingIds.has(String(teacher.id)))
      .map((teacher) => ({
        id: teacher.id,
        name: teacher.adi || `Ogretmen #${teacher.id}`,
        phone: teacher?.telefon || '',
        email: teacher?.e_posta || '',
        lessons: [],
        totalRevenue: 0,
        lessonCount: 0,
        totalDuration: 0,
        students: new Set(),
        studentCount: 0,
        averageRevenue: 0
      }));

    return [...teacherDetails, ...missingTeachers].sort((a, b) => {
      const revenueDiff = (b.totalRevenue || 0) - (a.totalRevenue || 0);
      if (revenueDiff !== 0) return revenueDiff;
      return (a.name || '').localeCompare(b.name || '', 'tr');
    });
  };

  useEffect(() => {
    if (isOpen) {
      setCurrentPage(1);
      fetchPaymentDetails();
    }
    
    // Cleanup on close
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (processingQueueRef.current) {
        clearTimeout(processingQueueRef.current);
      }
    };
  }, [isOpen, filterType, selectedMonth, selectedYear]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  // Sayfalama için verileri filtrele
  useEffect(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaymentDetails(filteredPaymentDetails.slice(startIndex, endIndex));
  }, [currentPage, filteredPaymentDetails, itemsPerPage]);

  // Öğretmenleri çekme fonksiyonu
  const fetchTeachers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axiosConfig.get('/admin/teachers', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      let teachersData = [];
      if (Array.isArray(response.data)) {
        teachersData = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        teachersData = response.data.data;
      }
      
      const activeTeachers = teachersData.filter(isTeacherActive);
      setTeachers(activeTeachers);
      return activeTeachers;
    } catch (error) {
      console.error('Öğretmenler yüklenemedi:', error);
      return [];
    }
  };

  // Batch processing function - Filtre tipine göre optimize edilmiş
  const processBatch = (lessons, teacherList = [], batchSize = 100) => {
    return new Promise((resolve) => {
      const teacherMap = {};
      const teacherLookup = teacherList.reduce((lookup, teacher) => {
        lookup[String(teacher.id)] = teacher;
        return lookup;
      }, {});
      let totalLessonRevenue = 0;
      let totalCompanyRevenue = 0;
      let totalConsultantPayments = 0;
      const chunks = [];
      
      // Filtre tipine göre akıllı batch boyutu
      let optimizedBatchSize = batchSize;
      if (filterType === 'weekly') {
        optimizedBatchSize = 75; // Haftalık için orta boyut
      } else if (filterType === 'monthly') {
        optimizedBatchSize = 50; // Aylık için daha küçük
      } else if (filterType === 'yearly' || filterType === 'all') {
        optimizedBatchSize = 25; // Yıllık ve tüm zamanlar için en küçük
      }
      
      // Veriyi chunk'lara böl
      for (let i = 0; i < lessons.length; i += optimizedBatchSize) {
        chunks.push(lessons.slice(i, i + optimizedBatchSize));
      }
      
      let currentChunkIndex = 0;
      
      const processNextChunk = () => {
        if (currentChunkIndex >= chunks.length) {
          // İşlem tamamlandı
          const teacherDetails = Object.values(teacherMap)
            .map(teacher => ({
              ...teacher,
              studentCount: teacher.students.size,
              averageRevenue: teacher.lessonCount > 0 ? teacher.totalRevenue / teacher.lessonCount : 0
            }))
            .sort((a, b) => b.totalRevenue - a.totalRevenue);
          
          resolve({
            teacherDetails,
            totalLessonRevenue,
            totalCompanyRevenue,
            totalConsultantPayments
          });
          return;
        }
        
        const chunk = chunks[currentChunkIndex];
        
        // Bu chunk'ı işle
        chunk.forEach(lesson => {
          const teacherId = lesson.ogretmen_id;
          const teacher = teacherLookup[String(teacherId)];
          const teacherName = teacher?.adi || lesson.ogretmen_adi || lesson.ogretmen_name || `Öğretmen #${teacherId}`;
          
          if (!teacherMap[teacherId]) {
            teacherMap[teacherId] = {
              id: teacherId,
              name: teacherName,
              lessons: [],
              totalRevenue: 0,
              lessonCount: 0,
              totalDuration: 0,
              students: new Set()
            };
          }

          const lessonRevenue = parseFloat(lesson.ders_ucret) || 0;
          const companyRevenue = parseFloat(lesson.sirket_payi) || 0;
          const consultantRevenue = (parseFloat(lesson.danisman1) || 0) + 
                                     (parseFloat(lesson.danisman2) || 0) + 
                                     (parseFloat(lesson.danisman3) || 0);

          teacherMap[teacherId].lessons.push({
            ...lesson,
            lessonRevenue,
            companyRevenue,
            consultantRevenue,
            totalRevenue: lessonRevenue + companyRevenue + consultantRevenue
          });

          teacherMap[teacherId].totalRevenue += lessonRevenue;
          teacherMap[teacherId].lessonCount++;
          teacherMap[teacherId].totalDuration += parseFloat(lesson.ders_sure) || 0;
          if (lesson.ogrenci_name) {
            teacherMap[teacherId].students.add(lesson.ogrenci_name);
          }

          totalLessonRevenue += lessonRevenue;
          totalCompanyRevenue += companyRevenue;
          totalConsultantPayments += consultantRevenue;
        });
        
        // Progress güncelle
        currentChunkIndex++;
        const progress = Math.round((currentChunkIndex / chunks.length) * 100);
        setLoadingProgress(progress);
        
        // Filtre tipine göre processing hızı ayarla
        let delay = 10;
        if (filterType === 'weekly') {
          delay = 15; // Haftalık için biraz daha yavaş
        } else if (filterType === 'monthly') {
          delay = 20; // Aylık için orta hız
        } else if (filterType === 'yearly' || filterType === 'all') {
          delay = 25; // Yıllık için en yavaş ama en stabil
        }
        
        // UI'ı dondurmamak için setTimeout kullan
        processingQueueRef.current = setTimeout(processNextChunk, delay);
      };
      
      // İşlemi başlat
      processNextChunk();
    });
  };

  const fetchPaymentDetails = async () => {
    setLoading(true);
    setIsProcessing(true);
    setLoadingProgress(0);
    
    // Önceki işlemleri iptal et
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    const isLargeDataSet = filterType === 'yearly' || filterType === 'all';
    
    try {
      const token = localStorage.getItem('token');
      let params = {};
      const today = new Date();
      const monthNumber = Number(selectedMonth);
      const yearNumber = Number(selectedYear);
      let teacherList = teachers;

      if (!Array.isArray(teacherList) || teacherList.length === 0) {
        teacherList = await fetchTeachers();
      }

      if (filterType === 'daily') {
        const todayStr = today.getFullYear() + '-' + 
               String(today.getMonth() + 1).padStart(2, '0') + '-' + 
               String(today.getDate()).padStart(2, '0');
        params = {
          start_date: todayStr,
          end_date: todayStr,
          limit: 1000
        };
      } else if (filterType === 'weekly') {
        const startOfWeek = new Date(today);
        const dayOfWeek = startOfWeek.getDay();
        const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        startOfWeek.setDate(diff);
        
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        
        params = {
          start_date: formatDateLocal(startOfWeek),
          end_date: formatDateLocal(endOfWeek),
          limit: 5000
        };
      } else if (filterType === 'monthly') {
        params = {
          start_date: formatDateLocal(new Date(yearNumber, monthNumber - 1, 1)),
          end_date: formatDateLocal(new Date(yearNumber, monthNumber, 0)),
          limit: 10000
        };
      } else if (filterType === 'yearly') {
        params = {
          start_date: formatDateLocal(new Date(yearNumber, 0, 1)),
          end_date: formatDateLocal(new Date(yearNumber, 11, 31)),
          limit: 50000
        };
      } else {
        params = {
          limit: 100000
        };
      }

      const response = await axiosConfig.get('/admin/ogretmenders', {
        headers: { Authorization: `Bearer ${token}` },
        params,
        signal: abortControllerRef.current.signal
      });

      let lessons = [];
      if (response.data.data && Array.isArray(response.data.data)) {
        lessons = response.data.data;
      } else if (Array.isArray(response.data)) {
        lessons = response.data;
      }

      const activeTeacherIdSet = new Set(teacherList.map((teacher) => String(teacher.id)));
      const activeTeacherLessons = lessons.filter((lesson) =>
        activeTeacherIdSet.has(String(lesson.ogretmen_id))
      );

      setTotalToProcess(activeTeacherLessons.length);
      
      // Filtre tipine göre batch processing eşiği
      let processingThreshold = 200;
      if (filterType === 'weekly') {
        processingThreshold = 150; // Haftalık için 150+ kayıtta batch
      } else if (filterType === 'monthly') {
        processingThreshold = 100; // Aylık için 100+ kayıtta batch
      } else if (filterType === 'yearly' || filterType === 'all') {
        processingThreshold = 50; // Yıllık için 50+ kayıtta batch
      }
      
      // Büyük veri setleri için batch processing kullan
      if (activeTeacherLessons.length > processingThreshold) {
        const result = await processBatch(activeTeacherLessons, teacherList); // Otomatik batch boyutu
        const mergedDetails = mergeWithAllActiveTeachers(result.teacherDetails, teacherList);

        setAllPaymentDetails(mergedDetails);
        setCurrentPage(1);
        
        setTotalStats({
          totalLessonRevenue: result.totalLessonRevenue,
          totalCompanyRevenue: result.totalCompanyRevenue,
          totalConsultantPayments: result.totalConsultantPayments,
          lessonCount: activeTeacherLessons.length
        });
      } else {
        // Küçük veri setleri için normal işleme
        const teacherMap = {};
        const teacherLookup = teacherList.reduce((lookup, teacher) => {
          lookup[String(teacher.id)] = teacher;
          return lookup;
        }, {});
        let totalLessonRevenue = 0;
        let totalCompanyRevenue = 0;
        let totalConsultantPayments = 0;

        activeTeacherLessons.forEach(lesson => {
          const teacherId = lesson.ogretmen_id;
          const teacher = teacherLookup[String(teacherId)];
          const teacherName = teacher?.adi || lesson.ogretmen_adi || lesson.ogretmen_name || `Öğretmen #${teacherId}`;
          
          if (!teacherMap[teacherId]) {
            teacherMap[teacherId] = {
              id: teacherId,
              name: teacherName,
              lessons: [],
              totalRevenue: 0,
              lessonCount: 0,
              totalDuration: 0,
              students: new Set()
            };
          }

          const lessonRevenue = parseFloat(lesson.ders_ucret) || 0;
          const companyRevenue = parseFloat(lesson.sirket_payi) || 0;
          const consultantRevenue = (parseFloat(lesson.danisman1) || 0) + 
                                     (parseFloat(lesson.danisman2) || 0) + 
                                     (parseFloat(lesson.danisman3) || 0);

          teacherMap[teacherId].lessons.push({
            ...lesson,
            lessonRevenue,
            companyRevenue,
            consultantRevenue,
            totalRevenue: lessonRevenue + companyRevenue + consultantRevenue
          });

          teacherMap[teacherId].totalRevenue += lessonRevenue;
          teacherMap[teacherId].lessonCount++;
          teacherMap[teacherId].totalDuration += parseFloat(lesson.ders_sure) || 0;
          if (lesson.ogrenci_name) {
            teacherMap[teacherId].students.add(lesson.ogrenci_name);
          }

          totalLessonRevenue += lessonRevenue;
          totalCompanyRevenue += companyRevenue;
          totalConsultantPayments += consultantRevenue;
        });

        const teacherDetails = Object.values(teacherMap)
          .map(teacher => ({
            ...teacher,
            studentCount: teacher.students.size,
            averageRevenue: teacher.lessonCount > 0 ? teacher.totalRevenue / teacher.lessonCount : 0
          }))
          .sort((a, b) => b.totalRevenue - a.totalRevenue);

        const mergedDetails = mergeWithAllActiveTeachers(teacherDetails, teacherList);
        setAllPaymentDetails(mergedDetails);
        setCurrentPage(1);
        
        setTotalStats({
          totalLessonRevenue,
          totalCompanyRevenue,
          totalConsultantPayments,
          lessonCount: activeTeacherLessons.length
        });
      }
      
      if (isLargeDataSet && activeTeacherLessons.length > 50) {
        console.log(`${activeTeacherLessons.length} ders verisi başarıyla yüklendi`);
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Ödeme detayları alınamadı:', error);
      }
    } finally {
      setLoading(false);
      setIsProcessing(false);
      setLoadingProgress(100);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getDateRangeText = () => {
    const today = new Date();
    if (filterType === 'daily') {
      return today.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' });
    } else if (filterType === 'weekly') {
      const startOfWeek = new Date(today);
      const dayOfWeek = startOfWeek.getDay();
      const diff = startOfWeek.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      startOfWeek.setDate(diff);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6);
      
      return `${startOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} - ${endOfWeek.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (filterType === 'monthly') {
      const selectedMonthLabel = MONTH_OPTIONS.find((month) => month.value === Number(selectedMonth))?.label || '';
      return `${selectedMonthLabel} ${selectedYear}`;
    } else if (filterType === 'yearly') {
      return `${selectedYear} Yili`;
    } else {
      return 'Tum Zamanlar';
    }
  };

  const handleSetCurrentMonth = () => {
    setSelectedMonth(currentMonth);
    setSelectedYear(currentYear);
  };

  const handleSetCurrentYear = () => {
    setSelectedYear(currentYear);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 flex items-center">
              <FiDollarSign className="mr-2 text-purple-600" />
              Öğretmen Ders Ücreti Detayları
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {getDateRangeText()} - {totalStats.lessonCount} ders
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <FiX className="text-2xl text-gray-600" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterType('daily')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'daily'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Gunluk
            </button>
            <button
              onClick={() => setFilterType('weekly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'weekly'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Haftalik
            </button>
            <button
              onClick={() => setFilterType('monthly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'monthly'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Aylik
            </button>
            <button
              onClick={() => setFilterType('yearly')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'yearly'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Yillik
            </button>
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                filterType === 'all'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
              }`}
            >
              Tum Zamanlar
            </button>
          </div>

          {(filterType === 'monthly' || filterType === 'yearly') && (
            <div className="bg-slate-100 rounded-lg p-3 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-gray-700">
                {filterType === 'monthly' ? 'Ay/Yil Sec:' : 'Yil Sec:'}
              </span>

              {filterType === 'monthly' && (
                <select
                  value={selectedMonth}
                  onChange={(event) => setSelectedMonth(Number(event.target.value))}
                  className="px-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  {MONTH_OPTIONS.map((month) => (
                    <option key={month.value} value={month.value}>
                      {month.label}
                    </option>
                  ))}
                </select>
              )}

              <select
                value={selectedYear}
                onChange={(event) => setSelectedYear(Number(event.target.value))}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>

              {filterType === 'monthly' ? (
                <button
                  onClick={handleSetCurrentMonth}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  Bu Ay
                </button>
              ) : (
                <button
                  onClick={handleSetCurrentYear}
                  className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 transition-colors"
                >
                  Bu Yil
                </button>
              )}
            </div>
          )}

          <div className="w-full md:max-w-lg">
            <label className="block text-sm font-semibold text-gray-700 mb-2">Ogretmen Ara</label>
            <div className="relative">
              <FiSearch className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Ad, telefon veya e-posta ile ara..."
                className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50">
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Ogretmen Ucreti</p>
                <p className="text-xl font-bold text-purple-600">
                  {formatCurrency(totalStats.totalLessonRevenue)}
                </p>
              </div>
              <FiUser className="text-purple-600 text-2xl" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Sirket Payi</p>
                <p className="text-xl font-bold text-orange-600">
                  {formatCurrency(totalStats.totalCompanyRevenue)}
                </p>
              </div>
              <FiDollarSign className="text-orange-600 text-2xl" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Danisman Odemeleri</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatCurrency(totalStats.totalConsultantPayments)}
                </p>
              </div>
              <FiUsers className="text-blue-600 text-2xl" />
            </div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Toplam Ciro</p>
                <p className="text-xl font-bold text-green-600">
                  {formatCurrency(
                    totalStats.totalLessonRevenue +
                    totalStats.totalCompanyRevenue +
                    totalStats.totalConsultantPayments
                  )}
                </p>
              </div>
              <FiTrendingUp className="text-green-600 text-2xl" />
            </div>
          </div>
        </div>
        {/* Pagination Controls - Top */}
        {filteredPaymentDetails.length > itemsPerPage && (
          <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Toplam <span className="font-semibold">{filteredPaymentDetails.length}</span> öğretmen
              <span className="ml-2">(
                {((currentPage - 1) * itemsPerPage) + 1} - 
                {Math.min(currentPage * itemsPerPage, filteredPaymentDetails.length)} arası gösteriliyor
              )</span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Önceki
              </button>
              <span className="px-3 py-1 text-sm">
                Sayfa {currentPage} / {Math.ceil(filteredPaymentDetails.length / itemsPerPage)}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredPaymentDetails.length / itemsPerPage)))}
                disabled={currentPage === Math.ceil(filteredPaymentDetails.length / itemsPerPage)}
                className="px-3 py-1 text-sm bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Sonraki →
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="flex-1 overflow-y-auto p-4">
          {isProcessing ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
              <p className="text-gray-600">Veriler işleniyor...</p>
              <p className="text-sm text-gray-500 mt-1">
                {totalToProcess > 0 ? `${totalToProcess} kayıt işleniyor` : 'Büyük veri seti yükleniyor'}
              </p>
              {loadingProgress > 0 && loadingProgress < 100 && (
                <div className="w-64 mt-4">
                  <div className="bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${loadingProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-500 text-center mt-1">%{loadingProgress}</p>
                </div>
              )}
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
            </div>
          ) : paymentDetails.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500">
                {searchTerm.trim()
                  ? 'Aramanıza uygun öğretmen bulunamadı.'
                  : 'Bu döneme ait ücret detayı bulunamadı.'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {paymentDetails.map((teacher) => (
                <div key={teacher.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="bg-gradient-to-r from-purple-100 to-pink-100 p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800">{teacher.name}</h3>
                        <div className="flex gap-4 mt-2 text-sm text-gray-600">
                          <span className="flex items-center">
                            <FiCalendar className="mr-1" />
                            {teacher.lessonCount} ders
                          </span>
                          <span className="flex items-center">
                            <FiClock className="mr-1" />
                            {teacher.totalDuration} dk
                          </span>
                          <span className="flex items-center">
                            <FiUser className="mr-1" />
                            {teacher.studentCount} öğrenci
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">
                          {formatCurrency(teacher.totalRevenue)}
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          Ort: {formatCurrency(teacher.averageRevenue)}/ders
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Ders Detayları */}
                  <div className="p-4">
                    <details className="group">
                      <summary className="cursor-pointer text-sm text-gray-600 hover:text-purple-600 transition-colors">
                        Ders detaylarını göster ({teacher.lessons.length} ders)
                      </summary>
                      <div className="mt-3 space-y-2">
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead className="text-xs text-gray-500 border-b">
                              <tr>
                                <th className="text-left py-2">Öğrenci</th>
                                <th className="text-left py-2">Konu</th>
                                <th className="text-center py-2">Süre</th>
                                <th className="text-right py-2">Ücret</th>
                                <th className="text-right py-2">Şirket</th>
                                <th className="text-right py-2">Danışman</th>
                                <th className="text-right py-2">Toplam</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                              {teacher.lessons.map((lesson, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="py-2 text-gray-700">{lesson.ogrenci_name || '-'}</td>
                                  <td className="py-2 text-gray-600">{lesson.ders_konusu || '-'}</td>
                                  <td className="py-2 text-center text-gray-600">{lesson.ders_sure || 0} dk</td>
                                  <td className="py-2 text-right font-medium text-purple-600">
                                    {formatCurrency(lesson.lessonRevenue)}
                                  </td>
                                  <td className="py-2 text-right text-orange-600">
                                    {formatCurrency(lesson.companyRevenue)}
                                  </td>
                                  <td className="py-2 text-right text-blue-600">
                                    {formatCurrency(lesson.consultantRevenue)}
                                  </td>
                                  <td className="py-2 text-right font-semibold text-green-600">
                                    {formatCurrency(lesson.totalRevenue)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </details>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 p-4 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            {filteredPaymentDetails.length > itemsPerPage ? (
              <span>
                Sayfa {currentPage} / {Math.ceil(filteredPaymentDetails.length / itemsPerPage)} -
                Toplam <span className="font-semibold">{filteredPaymentDetails.length}</span> öğretmen
              </span>
            ) : (
              <span>Toplam <span className="font-semibold">{filteredPaymentDetails.length}</span> öğretmen</span>
            )}
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherPaymentDetailsModal;

